import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Sprout } from "lucide-react";
import { toast } from "sonner";
import { GFFrame } from "@/components/GFFrame";
import { GENDERS, AGE_RANGES } from "@/data/gfContent";
import {
  PG_ROLES, PG_EMPLOYMENT, PG_EXPERIENCE, PG_EDUCATION, PG_RESPONSIBILITY, PG_REASON,
} from "@/data/personalContent";

const EMERALD = "#064E3B";

const PG_LIFE_STAGE = [
  "Pelajar", "Memulakan kerjaya", "Profesional bekerja", "Pengurus / pemimpin",
  "Usahawan / pemilik bisnes", "Peralihan kerjaya", "Kembali bekerja", "Lain-lain",
];
const PG_PRIORITY = [
  "Kerjaya", "Keyakinan diri", "Arah / kejelasan", "Hubungan", "Kepimpinan",
  "Pembangunan diri", "Keseimbangan kerja-hidup", "Memulakan sesuatu yang baharu", "Lain-lain",
];
const PG_ENERGY = [
  "Kerja", "Keluarga", "Kewangan", "Kesihatan", "Hubungan", "Pengajian", "Diri sendiri", "Lain-lain",
];

export const PGHeader = () => (
  <div className="flex items-center justify-center gap-2 pt-7 pb-2">
    <Sprout className="h-4 w-4 text-[#047857]" />
    <span className="font-heading text-sm font-bold tracking-[0.3em]" style={{ color: EMERALD }}>
      PERSONAL GROWTH
    </span>
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-slate-600">{label}</label>
    {children}
  </div>
);

const inputCls =
  "w-full rounded-2xl border border-[#DCE7E1] bg-white px-4 py-3.5 text-[15px] text-[#064E3B] outline-none transition-colors focus:border-[#047857] focus:ring-2 focus:ring-[#047857]/20";

const Sel = ({ testId, value, onChange, placeholder, options }) => (
  <select data-testid={testId} className={inputCls} value={value} onChange={onChange}>
    <option value="">{placeholder}</option>
    {options.map((o) => (<option key={o} value={o}>{o}</option>))}
  </select>
);

export default function PersonalInfo() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nama: "", gender: "", age_range: "", role: "", employment_status: "",
    experience: "", education: "", responsibility: "", reason: "",
    life_stage: "", main_priority: "", biggest_challenge: "", change_90d: "", energy_area: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = () => {
    const req = ["nama", "gender", "age_range", "role", "employment_status", "experience", "education", "responsibility", "reason"];
    if (req.some((k) => !form[k])) {
      toast.error("Sila lengkapkan semua maklumat.");
      return;
    }
    sessionStorage.setItem("gf_personal_user", JSON.stringify(form));
    navigate("/personal/soalan");
  };

  return (
    <GFFrame testId="personal-info-page">
      <div className="flex min-h-screen flex-col">
        <PGHeader />
        <button onClick={() => navigate("/")} data-testid="pg-back-home-btn"
          className="ml-6 mt-2 flex w-fit items-center gap-1 text-sm text-slate-400">
          <ArrowLeft className="h-4 w-4" /> Kembali
        </button>

        <div className="flex flex-1 flex-col px-6 pb-10 pt-4">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="mb-5 h-[3px] w-12 rounded-full bg-[#D4AF37]" />
            <h2 className="font-heading text-3xl font-bold leading-tight" style={{ color: EMERALD }}>
              Ceritakan sedikit tentang diri anda
            </h2>
            <p className="mt-3 text-sm text-slate-500">
              Maklumat ini membantu kami menyesuaikan laporan pertumbuhan diri anda. Tiada akaun diperlukan.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }} className="mt-8 space-y-5">
            <Field label="Nama">
              <input data-testid="pg-input-nama" className={inputCls} placeholder="Nama anda"
                value={form.nama} onChange={set("nama")} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Jantina"><Sel testId="pg-input-gender" value={form.gender} onChange={set("gender")} placeholder="Pilih" options={GENDERS} /></Field>
              <Field label="Julat Umur"><Sel testId="pg-input-age" value={form.age_range} onChange={set("age_range")} placeholder="Pilih" options={AGE_RANGES} /></Field>
            </div>
            <Field label="Peranan / Situasi Hidup"><Sel testId="pg-input-role" value={form.role} onChange={set("role")} placeholder="Pilih peranan" options={PG_ROLES} /></Field>
            <Field label="Status Pekerjaan"><Sel testId="pg-input-employment" value={form.employment_status} onChange={set("employment_status")} placeholder="Pilih status" options={PG_EMPLOYMENT} /></Field>
            <Field label="Pengalaman Kerja / Hidup"><Sel testId="pg-input-experience" value={form.experience} onChange={set("experience")} placeholder="Pilih pengalaman" options={PG_EXPERIENCE} /></Field>
            <Field label="Tahap Pendidikan Tertinggi"><Sel testId="pg-input-education" value={form.education} onChange={set("education")} placeholder="Pilih pendidikan" options={PG_EDUCATION} /></Field>
            <Field label="Tanggungjawab Utama Kini"><Sel testId="pg-input-responsibility" value={form.responsibility} onChange={set("responsibility")} placeholder="Pilih tanggungjawab" options={PG_RESPONSIBILITY} /></Field>
            <Field label="Sebab Utama Mengambil Penilaian Ini"><Sel testId="pg-input-reason" value={form.reason} onChange={set("reason")} placeholder="Pilih sebab" options={PG_REASON} /></Field>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }} className="mt-10" data-testid="kenali-diri-section">
            <div className="mb-4 h-[3px] w-12 rounded-full bg-[#D4AF37]" />
            <h3 className="font-heading text-xl font-bold" style={{ color: EMERALD }}>Kenali Diri</h3>
            <p className="mt-2 text-sm text-slate-500">
              Sedikit konteks kehidupan menjadikan laporan lebih peribadi. Ia tidak dinilai — kongsi apa yang selesa sahaja.
            </p>
            <div className="mt-6 space-y-5">
              <Field label="Fasa Hidup Semasa"><Sel testId="pg-input-life-stage" value={form.life_stage} onChange={set("life_stage")} placeholder="Pilih fasa hidup" options={PG_LIFE_STAGE} /></Field>
              <Field label="Keutamaan Utama Kini"><Sel testId="pg-input-priority" value={form.main_priority} onChange={set("main_priority")} placeholder="Pilih keutamaan" options={PG_PRIORITY} /></Field>
              <Field label="Cabaran Terbesar Kini">
                <textarea data-testid="pg-input-challenge" rows={2} className={inputCls}
                  placeholder="Tulis secara ringkas..." value={form.biggest_challenge} onChange={set("biggest_challenge")} />
              </Field>
              <Field label="Satu Perkara Paling Ingin Dicapai / Diubah Dalam 90 Hari">
                <textarea data-testid="pg-input-change-90d" rows={2} className={inputCls}
                  placeholder="Tulis secara ringkas..." value={form.change_90d} onChange={set("change_90d")} />
              </Field>
              <Field label="Bidang Hidup Paling Banyak Menyerap Tenaga (pilihan)"><Sel testId="pg-input-energy" value={form.energy_area} onChange={set("energy_area")} placeholder="Pilih (pilihan)" options={PG_ENERGY} /></Field>
            </div>
          </motion.div>

          <div className="mt-auto pt-8">
            <button data-testid="pg-continue-btn" onClick={submit}
              className="group flex w-full items-center justify-center gap-3 rounded-full py-4 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] hover:scale-[1.01]"
              style={{ background: EMERALD }}>
              MULA PENILAIAN
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </GFFrame>
  );
}
